// Composer — replaces the deleted hand-rolled SendComposer.tsx (auto-resize
// textarea, drag/drop, paste-file handling, attach button, file chips, stop
// button all hand-written). assistant-ui's ComposerPrimitive owns the
// textarea/attachment/cancel/send state machine natively; the attachment
// adapter (attachmentAdapter.ts) handles agenthub's upload-limit validation.
// Styled against the Claude-clone reference.
//
// All three input methods are native primitives, not hand-rolled: the
// attach button (AddAttachment), paste-to-attach (built into Input), and
// drag-and-drop (AttachmentDropzone, wrapping the whole composer as the
// drop target with `data-dragging` for the highlight below).

import { useEffect } from 'react';
import { AttachmentPrimitive, AuiIf, ComposerPrimitive, useAui, useAuiEvent, useAuiState } from '@assistant-ui/react';
import { AlertCircleIcon, ArrowUpIcon, Loader2Icon, PlusIcon, XIcon } from 'lucide-react';
import { toast } from '@/shared/ui/sonner';
import { AttachmentPreviewDialog, PreviewTable, pickFileTypeIcon, useFilePreview } from '@/entities/file';
import { cn } from '@/shared/lib/cn';
import { ComposerQuotePreview } from '@/shared/ui/quote';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

const iconButtonClassName =
  'flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50';

// Surfaces attachmentAdapter.ts's validation rejections (throws from add()) —
// without this subscription they fail silently (emitted event, nobody home).
function useAttachmentErrorToast() {
  useAuiEvent('composer.attachmentAddError', ({ message }) => {
    toast.error(message, { duration: 5_000 });
  });
}

// AttachmentByIndex sets up a scoped "current attachment" context internally
// before rendering `components.Attachment` — AttachmentPrimitive.Name/Remove
// read from that context, not from props. The tile shows a thumbnail/icon
// only; the filename lives in a hover tooltip instead of always-visible
// text (jarring on longer names), and a real preview opens full-size on
// click (image/PDF/table types — everything else stays icon-only, see
// useFilePreview.ts).
function AttachmentTile() {
  const status = useAuiState((s) => s.attachment.status);
  const name = useAuiState((s) => s.attachment.name);
  const contentType = useAuiState((s) => s.attachment.contentType);
  const file = useAuiState((s) => s.attachment.file);
  const isUploading = status.type === 'running' && status.reason === 'uploading';
  const isError = status.type === 'incomplete' && status.reason === 'error';
  const preview = useFilePreview(file, contentType);
  const Icon = pickFileTypeIcon(contentType, name);

  return (
    <Tooltip>
      <AttachmentPrimitive.Root className="relative">
        <AttachmentPreviewDialog preview={preview} name={name}>
          <TooltipTrigger asChild>
            <div
              role="button"
              tabIndex={0}
              className={cn(
                'relative size-14 shrink-0 overflow-hidden rounded-md bg-secondary ring-1 ring-border',
                isError && 'ring-destructive/60'
              )}
            >
              {preview.kind === 'image' && <img src={preview.src} alt="" className="size-full object-cover" />}
              {preview.kind === 'pdf' && (
                // ponytail: cropped native PDF viewer, not a rasterized first-page
                // thumbnail — upgrade to pdf.js-rendered canvas if this reads too blank.
                <embed src={preview.src} type="application/pdf" className="pointer-events-none size-full" />
              )}
              {preview.kind === 'table' && (
                <PreviewTable rows={preview.rows} className="pointer-events-none text-[6px] leading-tight" />
              )}
              {preview.kind === 'text' && (
                <pre className="pointer-events-none size-full overflow-hidden p-1 text-[6px] leading-tight break-all whitespace-pre-wrap text-muted-foreground">
                  {preview.snippet}
                </pre>
              )}
              {preview.kind === 'none' && (
                <div className="flex size-full items-center justify-center">
                  <Icon className="size-6 text-muted-foreground" />
                </div>
              )}
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[2px]">
                  <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {isError && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-[2px]">
                  <AlertCircleIcon className="size-4 text-destructive" />
                </div>
              )}
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
        <AttachmentPrimitive.Remove
          className={cn(
            iconButtonClassName,
            'absolute -top-1.5 -right-1.5 size-5 rounded-full bg-foreground text-background hover:bg-foreground/80 hover:text-background'
          )}
          aria-label="Remove attachment"
        >
          <XIcon className="size-3" />
        </AttachmentPrimitive.Remove>
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
}

function AttachmentList() {
  const count = useAuiState((s) => s.composer.attachments.length);
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <ComposerPrimitive.AttachmentByIndex key={index} index={index} components={{ Attachment: AttachmentTile }} />
      ))}
    </>
  );
}

export function SendComposer({ agentId, threadId }: { agentId: string; threadId?: string | null }) {
  useAttachmentErrorToast();
  const aui = useAui();
  const text = useAuiState((state) => state.composer.text);
  const clarificationPending = useAuiState((state) => state.thread.messages.some((message) => message.role === 'assistant'
    && message.content.some((part) => part.type === 'tool-call'
      && part.toolName.startsWith('Agent:')
      && part.messages?.some((nested) => nested.role === 'assistant'
        && nested.content.some((item) => item.type === 'data'
          && item.name === 'specialist_question'
          && !(item.data as { answered?: boolean }).answered)))));
  const draftKey = `agenthub:composer-draft:${agentId}:${threadId ?? 'new'}`;

  useEffect(() => {
    try {
      const draft = window.localStorage.getItem(draftKey);
      if (draft && !text) aui.composer().setText(draft);
    } catch {
      // Draft persistence is best-effort and must never block chat input.
    }
    // Thread.tsx swaps the empty-state composer for the footer composer when
    // the first message lands. An attachment send clears the text in that same
    // render, so the persist effect below never sees the empty text before
    // unmount. Read the live composer text here so the next instance cannot
    // restore an already-sent draft (#1424).
    return () => {
      try {
        if (!aui.composer().getState().text) window.localStorage.removeItem(draftKey);
      } catch {
        // Draft persistence is best-effort and must never block chat input.
      }
    };
    // Restore only when the selected thread changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  useEffect(() => {
    try {
      if (text) window.localStorage.setItem(draftKey, text);
      else window.localStorage.removeItem(draftKey);
    } catch {
      // Draft persistence is best-effort and must never block chat input.
    }
  }, [draftKey, text]);

  return (
    <ComposerPrimitive.AttachmentDropzone asChild>
      <ComposerPrimitive.Root className="flex w-full flex-col gap-2 rounded-2xl border border-border bg-background px-3.5 pt-3 pb-2.5 data-[dragging=true]:border-dashed data-[dragging=true]:border-primary data-[dragging=true]:bg-accent/40">
        <ComposerQuotePreview />
        {clarificationPending ? <p id="specialist-clarification-guidance" className="sr-only">Answer the specialist question above to continue.</p> : null}
        <ComposerPrimitive.Input
          placeholder={clarificationPending ? 'Answer the specialist question above to continue' : 'Type a message…'}
          rows={1}
          disabled={clarificationPending}
          aria-describedby={clarificationPending ? 'specialist-clarification-guidance' : undefined}
          className="block max-h-72 min-h-6 w-full resize-none bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
        />

        <AuiIf condition={(s) => s.composer.attachments.length > 0}>
          <div className="-mb-1 flex flex-row gap-3 overflow-x-auto p-1.5">
            <AttachmentList />
          </div>
        </AuiIf>

        <div className="flex w-full items-center gap-2">
          <ComposerPrimitive.AddAttachment aria-label="Add attachment" className={iconButtonClassName} disabled={clarificationPending}>
            <PlusIcon className="size-4" />
          </ComposerPrimitive.AddAttachment>

          <div className="ml-auto flex items-center gap-1">
            <AuiIf condition={(s) => s.thread.isRunning}>
              <ComposerPrimitive.Cancel
                className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:opacity-90"
                aria-label="Stop response"
              >
                <div className="size-2.5 rounded-[2px] bg-current" />
              </ComposerPrimitive.Cancel>
            </AuiIf>
            <AuiIf condition={(s) => !s.thread.isRunning}>
              <ComposerPrimitive.Send
                className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
                aria-label="Send message"
                disabled={clarificationPending}
              >
                <ArrowUpIcon className="size-4" />
              </ComposerPrimitive.Send>
            </AuiIf>
          </div>
        </div>
      </ComposerPrimitive.Root>
    </ComposerPrimitive.AttachmentDropzone>
  );
}
